/**
 * AnalysisErrorCard — rendered inside an analysis phase (in place of
 * the running spinner) when the backend has persisted an agent
 * failure (`error_message` on the residual progress document).
 *
 * Retry funnels through the existing POST /continue contract: the
 * backend clears the error, re-derives the phase from data and
 * re-runs only the items that never completed (current_* still
 * null) — finished work is never redone.
 */
import React, { useState } from 'react';
import { Button } from '../../../../components/ui/Button2';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../../../../components/ui/Empty';
import { Icon } from '../../../../components/ui/Icon';

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors
// the ButtonAny convention across the phase components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

interface AnalysisErrorCardProps {
  title?: string;
  message: string;
  onRetry: () => Promise<void> | void;
}

export default function AnalysisErrorCard({
  title = 'Analysis failed',
  message,
  onRetry,
}: AnalysisErrorCardProps) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon name="warning" tone="danger" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{message}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <ButtonAny onClick={handleRetry} disabled={retrying}>
          {retrying ? 'Retrying…' : 'Retry analysis'}
        </ButtonAny>
      </EmptyContent>
    </Empty>
  );
}
